import type { AudioSettings } from "./audio-settings";
import type { AudioFrame } from "./snapshot-audio";
import { MUSIC_FILES, SOUND_FILES } from "./sound-catalog";
import type { MusicScene, SoundCue, SoundId } from "./sound-catalog";

export type AudioStatus = "waiting" | "ready" | "unavailable";
interface Loop { source: AudioBufferSourceNode; gain: GainNode }
export const MAX_SOUND_VOICES = 12;
const WEAPONS = new Set<SoundId>(["pulse", "laser", "missile", "flak", "rail", "ion", "mine"]);
const INTERFACE = new Set<SoundId>(["click", "select", "pickup", "warning"]);
const DUCK_DB: Partial<Record<SoundId, number>> = {
  shield: -3, hull: -4, explosion: -6, warning: -6, ability: -2, jump: -4,
};
const gainFromDb = (decibels: number) => 10 ** (decibels / 20);
const PEAK_GUARD_CURVE = Float32Array.from({ length: 4097 }, (_, index) => {
  const value = index / 2048 - 1;
  const magnitude = Math.abs(value);
  return magnitude <= .65 ? value : Math.sign(value) * (.65 + .19 * Math.tanh((magnitude - .65) / .19));
});
const priority = (id: SoundId) => WEAPONS.has(id) ? 2 : id === "shield" || id === "hull" ? 1 : 0;
interface Voice { gain: GainNode; priority: number }

/** One browser audio graph, owned and disposed by the mounted game. */
export class GameAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private musicDuck: GainNode | null = null;
  private effectsBus: GainNode | null = null;
  private interfaceBus: GainNode | null = null;
  private ambienceBus: GainNode | null = null;
  private shipBus: GainNode | null = null;
  private engine: Loop | null = null;
  private music: Loop | null = null;
  private requestedMusic: MusicScene | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly loads = new Map<string, Promise<AudioBuffer | null>>();
  private readonly cooldowns = new Map<SoundId, number>();
  private readonly voices = new Map<AudioBufferSourceNode, Voice>();
  private readonly lastSamples = new Map<SoundId, string>();
  private duckUntil = 0;
  private duckLevel = 0;
  private readonly abort = new AbortController();
  private active = true;
  private disposed = false;
  private frame: AudioFrame = { cues: [], music: "station", engine: 0 };

  constructor(private settings: AudioSettings, private readonly onStatus: (status: AudioStatus) => void) {}

  async unlock(): Promise<void> {
    if (this.disposed || !this.active) return;
    if (!this.context) {
      if (typeof AudioContext === "undefined") { this.onStatus("unavailable"); return; }
      try {
        const context = new AudioContext({ latencyHint: "interactive" });
        this.context = context;
        this.master = context.createGain();
        this.musicBus = context.createGain();
        this.effectsBus = context.createGain();
        this.musicDuck = context.createGain();
        this.interfaceBus = context.createGain();
        this.ambienceBus = context.createGain();
        this.shipBus = context.createGain();
        this.shipBus.gain.value = gainFromDb(4);
        const limiter = context.createDynamicsCompressor();
        limiter.threshold.value = -8;
        limiter.knee.value = 8;
        limiter.ratio.value = 12;
        limiter.attack.value = 0.003;
        limiter.release.value = 0.2;
        const peakGuard = context.createWaveShaper();
        peakGuard.curve = PEAK_GUARD_CURVE;
        peakGuard.oversample = "4x";
        this.musicBus.connect(this.musicDuck).connect(this.master);
        this.effectsBus.connect(this.master);
        this.interfaceBus.connect(this.effectsBus);
        this.ambienceBus.connect(this.effectsBus);
        this.shipBus.connect(this.effectsBus);
        this.master.connect(limiter);
        limiter.connect(peakGuard).connect(context.destination);
        this.applyVolumes();
        // Decode only after an interaction; dropped one-shots are never queued.
        void this.preloadEffects();
        void this.load(SOUND_FILES.engine[0]).then(() => this.syncEngine());
      } catch (error) {
        console.warn("audio-initialization-failed", { error: String(error) });
        this.onStatus("unavailable");
        return;
      }
    }
    try {
      if (this.context.state === "suspended") await this.context.resume();
      if (this.disposed) return;
      if (!this.active) {
        if (this.context.state === "running") await this.context.suspend();
        return;
      }
      this.onStatus(this.context.state === "running" ? "ready" : "waiting");
      this.syncMusic();
      this.syncEngine();
    } catch {
      // A browser may require another physical interaction after an interruption.
      if (!this.disposed) this.onStatus("waiting");
    }
  }

  configure(settings: AudioSettings): void {
    this.settings = settings;
    this.applyVolumes();
    this.syncMusic();
  }

  async preloadEffects(): Promise<ReadonlySet<string>> {
    await Promise.all(Object.values(SOUND_FILES).flat().map(file => this.load(file)));
    return new Set(this.buffers.keys());
  }

  setActive(active: boolean): void {
    this.active = active;
    if (!active && this.context?.state === "running")
      void this.context.suspend().then(() => {
        if (this.active && !this.disposed) void this.unlock();
      }).catch(error => {
        if (!this.disposed) console.warn("audio-suspend-failed", { error: String(error) });
      });
    else if (active && this.context) void this.unlock();
  }

  receive(frame: AudioFrame): void {
    this.frame = frame;
    this.syncMusic();
    this.syncEngine();
    for (const cue of frame.cues.toSorted((left, right) => priority(left.id) - priority(right.id))) this.play(cue);
  }

  play(cue: SoundCue): void {
    const context = this.context;
    if (!context || context.state !== "running" || !this.effectsBus || !this.active ||
      this.disposed || this.settings.muted || this.settings.effects <= 0 || cue.gain <= 0) return;
    const now = context.currentTime;
    if (now < (this.cooldowns.get(cue.id) ?? -1)) return;
    const available = SOUND_FILES[cue.id].filter(file => this.buffers.has(file));
    const alternatives = available.filter(file => file !== this.lastSamples.get(cue.id));
    const pool = alternatives.length ? alternatives : available;
    const file = pool[Math.floor(Math.random() * pool.length)];
    const buffer = file ? this.buffers.get(file) : null;
    if (!buffer || !file) return;
    if (this.voices.size >= MAX_SOUND_VOICES) {
      const victim = [...this.voices].find(([, voice]) => voice.priority > priority(cue.id));
      if (!victim) return;
      const [source, voice] = victim;
      this.fade(voice.gain, 0, .008);
      source.stop(now + .01);
      this.voices.delete(source);
    }
    this.lastSamples.set(cue.id, file);
    this.cooldowns.set(cue.id, now + (cue.id === "warning" ? 5 : cue.id === "explosion" ? 0.12 : 0.055));
    const source = context.createBufferSource();
    source.buffer = buffer;
    const varied = WEAPONS.has(cue.id) || cue.id === "shield" || cue.id === "hull";
    source.detune.value = varied ? (Math.random() * 2 - 1) * (WEAPONS.has(cue.id) ? 6 : 12) : 0;
    const gain = context.createGain();
    gain.gain.value = Math.max(0, Math.min(1, cue.gain)) * gainFromDb(varied ? (Math.random() * 2 - 1) * .6 : 0);
    const pan = context.createStereoPanner();
    pan.pan.value = Math.max(-1, Math.min(1, cue.pan));
    const bus = INTERFACE.has(cue.id) ? this.interfaceBus : this.shipBus;
    source.connect(gain).connect(pan).connect(bus ?? this.effectsBus);
    this.voices.set(source, { gain, priority: priority(cue.id) });
    source.onended = () => { this.voices.delete(source); source.disconnect(); gain.disconnect(); pan.disconnect(); };
    source.start();
    const duck = DUCK_DB[cue.id];
    if (duck !== undefined) this.duckMusic(duck, cue.id === "warning" || cue.id === "explosion" ? .65 : .22);
  }

  dispose(): void {
    this.disposed = true;
    this.abort.abort();
    this.voices.clear();
    this.buffers.clear();
    if (this.context) void this.context.close().catch(error =>
      console.warn("audio-close-failed", { error: String(error) }));
  }

  private async load(file: string): Promise<AudioBuffer | null> {
    const cached = this.loads.get(file);
    if (cached) return cached;
    const context = this.context;
    if (!context || this.disposed) return null;
    const loading = (async () => {
      try {
        const response = await fetch(`/assets/audio/${file}.ogg`, { signal: this.abort.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const buffer = await context.decodeAudioData(await response.arrayBuffer());
        if (this.disposed) return null;
        this.buffers.set(file, buffer);
        if (file.startsWith("music/")) this.trimMusicCache(file);
        return buffer;
      } catch (error) {
        if (!this.disposed) console.warn("audio-asset-failed", { file, error: String(error) });
        return null;
      }
    })();
    this.loads.set(file, loading);
    return loading;
  }

  private trimMusicCache(current: string): void {
    const music = [...this.buffers.keys()].filter(key => key.startsWith("music/"));
    for (const file of music.slice(0, Math.max(0, music.length - 2))) {
      if (file === current) continue;
      this.buffers.delete(file);
      this.loads.delete(file);
    }
  }

  private fade(node: GainNode | null, value: number, seconds = 0.08): void {
    if (!node || !this.context || this.disposed) return;
    const now = this.context.currentTime;
    node.gain.cancelAndHoldAtTime(now);
    node.gain.linearRampToValueAtTime(value, now + seconds);
  }

  private applyVolumes(): void {
    this.fade(this.master, this.settings.muted ? 0 : gainFromDb(-2));
    this.fade(this.musicBus, this.settings.music * (this.frame.music === "combat" ? 0.7 : 1));
    this.fade(this.effectsBus, this.settings.effects);
  }

  private duckMusic(decibels: number, hold: number): void {
    const context = this.context;
    const bus = this.musicDuck;
    if (!context || !bus) return;
    const now = context.currentTime;
    this.duckLevel = now < this.duckUntil ? Math.min(this.duckLevel, decibels) : decibels;
    this.duckUntil = Math.max(this.duckUntil, now + hold);
    bus.gain.cancelAndHoldAtTime(now);
    const gain = gainFromDb(this.duckLevel);
    bus.gain.linearRampToValueAtTime(gain, now + .012);
    bus.gain.setValueAtTime(gain, this.duckUntil);
    bus.gain.linearRampToValueAtTime(1, this.duckUntil + .45);
  }

  private loop(buffer: AudioBuffer, bus: GainNode): Loop | null {
    const context = this.context;
    if (!context || this.disposed) return null;
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = true;
    const gain = context.createGain();
    gain.gain.value = 0;
    source.connect(gain).connect(bus);
    source.onended = () => { source.disconnect(); gain.disconnect(); };
    source.start();
    return { source, gain };
  }

  private syncEngine(): void {
    if (!this.context || !this.ambienceBus || this.disposed) return;
    const buffer = this.buffers.get(SOUND_FILES.engine[0]);
    if (!this.engine && buffer) this.engine = this.loop(buffer, this.ambienceBus);
    this.fade(this.engine?.gain ?? null, this.frame.engine, 0.35);
  }

  private syncMusic(): void {
    if (!this.context || !this.musicBus || this.disposed || !this.active) return;
    this.applyVolumes();
    const scene = this.frame.music;
    if (this.requestedMusic === scene || this.settings.music <= 0 || this.settings.muted) return;
    this.requestedMusic = scene;
    void this.load(`music/${MUSIC_FILES[scene]}`).then(buffer => {
      if (!buffer || this.disposed || this.requestedMusic !== scene || !this.musicBus || !this.context) return;
      const previous = this.music;
      this.music = this.loop(buffer, this.musicBus);
      this.fade(this.music?.gain ?? null, 1, 1.8);
      if (previous) {
        this.fade(previous.gain, 0, 1.8);
        previous.source.stop(this.context.currentTime + 1.85);
      }
    });
  }
}
